/**
 * BandwagonExplanationScreen.tsx - Explanation of bandwagon effect
 * Last updated: 2025-08-14 13:37:00
 * Updated by: JulianPolly (glossary adaptation, multi-language)
 */

import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@blueprintjs/core";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors } from "../../../../components/colors";
import BandwagonImg from "./assets/bandwagon_effect.png";
import { de as deTranslations } from "../../../../translations/de";
import { en as enTranslations } from "../../../../translations/en";

// --- Helper: get glossary for current language ---
function useGlossaryTerms(language: string) {
    if (language === "de") {
        return deTranslations.glossary?.terms || {};
    } else if (language === "en") {
        return enTranslations.glossary?.terms || {};
    }
    // fallback: use German
    return deTranslations.glossary?.terms || {};
}

// --- Hilfsfunktion für Glossar-Alias-Auflösung (language-agnostic) ---
function resolveGlossarEntry(
    glossar: Record<string, any>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

// --- Tooltip für Glossar-Begriff, multi-language ---
const BegriffTooltip: React.FC<{ begriff: string; children: React.ReactNode; glossar: Record<string, any> }> = ({ begriff, children, glossar }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: colors.lichtBlauLighter,
                color: colors.brillantBlue,
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block"
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => {
                setVisible((v: boolean) => !v);
                e.stopPropagation();
            }}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 320,
                            maxWidth: 600,
                            background: colors.white,
                            color: colors.textDark,
                            border: `1.5px solid ${colors.lichtBlauLight}`,
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "22px 24px",
                            fontWeight: 400,
                            fontSize: 16,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-line",
                            marginTop: 12,
                            textAlign: "justify"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// --- Case-insensitive glossary parsing, multi-language ---
function parseWithGlossarTooltip(
    text: string,
    terms: string[],
    used: Set<string>,
    glossar: Record<string, any>
) {
    if (typeof text !== "string") return null;
    const glossaryKeyMap: Record<string, string> = {};
    terms.forEach(k => { glossaryKeyMap[k.toLowerCase()] = k; });

    const re = new RegExp(
        `\\b(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
        "gi"
    );
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let i = 0;

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const matched = match[1];
        const glossaryKey = glossaryKeyMap[matched.toLowerCase()] || matched;

        if (!used.has(glossaryKey)) {
            used.add(glossaryKey);
            parts.push(
                <BegriffTooltip begriff={glossaryKey} glossar={glossar} key={glossaryKey + "-" + i}>
                    {matched}
                </BegriffTooltip>
            );
        } else {
            parts.push(matched);
        }
        lastIndex = match.index + matched.length;
        i++;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
}

export function BandwagonExplanationScreen(props: ScreenProps) {
    const { t, language } = useLanguage();
    const glossar = useGlossaryTerms(language);
    const terms = Object.keys(glossar).sort((a, b) => b.length - a.length);
    const used = new Set<string>();

    useEffect(() => {
        props.setCanContinue(true);
    }, [props]);

    // Get section titles from translations
    const sectionTitles = [
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'whatIsIt', 'title'),
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'howItAffects', 'title'),
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'whyFollow', 'title'),
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'research', 'title')
    ];

    // Get paragraphs from translations
    const paragraphs = [
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'whatIsIt', 'content'),
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'howItAffects', 'content'),
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'whyFollow', 'content'),
        t('experiments', 'bandwagon', 'screens', 'explanation', 'sections', 'research', 'content')
    ];

    // Get sources from translations
    const sourcesAPA = t('experiments', 'bandwagon', 'screens', 'explanation', 'sources', 'items', { returnObjects: true }) as string[];

    // Generate content with sections
    const parsedContent: React.ReactNode[] = [];
    for (let i = 0; i < sectionTitles.length; ++i) {
        parsedContent.push(
            <div key={"section-title-" + i} style={{
                marginTop: i === 0 ? 0 : 30,
                marginBottom: 8
            }}>
                <b style={{
                    fontSize: 20,
                    color: colors.brillantBlue,
                    fontWeight: 600
                }}>
                    {sectionTitles[i]}
                </b>
            </div>
        );

        // Bild nur im ersten Abschnitt, oben rechts
        if (i === 0) {
            parsedContent.push(
                <img
                    src={BandwagonImg}
                    alt={t('experiments', 'bandwagon', 'title')}
                    style={{
                        float: "right",
                        marginLeft: 44,
                        marginBottom: 24,
                        width: 340, // Größeres Bild: von 260 auf 340 erhöht
                        maxWidth: "40%", // Maximalbreite von 30% auf 40% erhöht
                        borderRadius: 16,
                        boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                        objectFit: "cover"
                    }}
                />
            );
        }

        parsedContent.push(
            <p key={`p-${i}`} style={{
                marginTop: 0,
                marginBottom: 18,
                whiteSpace: "pre-line",
                color: colors.textMedium,
                fontSize: 17,
                lineHeight: 1.7,
                textAlign: "justify",
                textJustify: "inter-word"
            }}>
                {parseWithGlossarTooltip(paragraphs[i], terms, used, glossar)}
            </p>
        );
    }

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            {/* Main content card */}
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 36px",
                marginBottom: 28
            }}>
                {/* Heading */}
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t('experiments', 'bandwagon', 'screens', 'explanation', 'title')}
                </h2>

                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    position: "relative",
                    minHeight: 320
                }}>
                    {parsedContent}
                </div>

                {/* Experiment note */}
                <div style={{
                    marginTop: 38,
                    marginBottom: 38,
                    background: colors.backgroundLight,
                    borderRadius: 11,
                    padding: "26px 32px",
                    fontSize: 17,
                    lineHeight: 1.7,
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)",
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: 16
                    }}>
                        <Icon
                            icon="lab-test"
                            size={20}
                            style={{
                                marginRight: 12,
                                marginTop: 2,
                                color: colors.brillantBlue
                            }}
                        />
                        <span style={{
                            fontWeight: 600,
                            fontSize: 18,
                            color: colors.brillantBlue
                        }}>
                            {t('experiments', 'bandwagon', 'screens', 'explanation', 'experiment', 'title')}
                        </span>
                    </div>
                    {t('experiments', 'bandwagon', 'screens', 'explanation', 'experiment', 'content')}
                </div>

                {/* Sources */}
                <div style={{
                    background: colors.backgroundLight,
                    borderRadius: 8,
                    padding: "22px 24px",
                    marginTop: 36,
                    fontSize: 15.5,
                    color: colors.textMedium
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 8
                    }}>
                        <Icon
                            icon="book"
                            size={16}
                            style={{
                                marginRight: 10,
                                color: colors.brillantBlue
                            }}
                        />
                        <span style={{
                            fontWeight: 600,
                            color: colors.brillantBlue
                        }}>
                            {t('experiments', 'bandwagon', 'screens', 'explanation', 'sources', 'title')}
                        </span>
                    </div>
                    <ul style={{
                        marginTop: 8,
                        marginBottom: 0,
                        paddingLeft: 24,
                        textAlign: "justify",
                        textJustify: "inter-word"
                    }}>
                        {sourcesAPA.map((src, index) => (
                            <li key={`source-${index}`} style={{
                                marginBottom: index < sourcesAPA.length - 1 ? 8 : 0
                            }}>
                                {src}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export const BandwagonExplanationScreenConfig: ScreenConfig = {
    component: BandwagonExplanationScreen,
    defaultResult: {},
    canContinueDefault: true,
};